'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-violet-500 flex-shrink-0">
    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const CheckIconWhite = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-violet-200 flex-shrink-0">
    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

export default function PricingPage() {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');

  const basicPrice = billing === 'monthly' ? '$5.99' : '$59.90';
  const advancedPrice = billing === 'monthly' ? '$9.99' : '$99.90';
  const period = billing === 'monthly' ? '/month' : '/year';

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Header />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 bg-gray-50 text-center">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-3">Simple, transparent pricing</h1>
          <p className="text-gray-500">Start free. Upgrade whenever you&apos;re ready.</p>
        </div>
      </section>
      {/* Billing Toggle */}
      <section className="pt-10 pb-2 px-6 bg-white">
        <div className="flex items-center justify-center gap-3">
          <span className={`text-sm font-medium ${billing === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
          <button
            type="button"
            onClick={() => setBilling(billing === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${billing === 'yearly' ? 'bg-violet-600' : 'bg-gray-200'}`}
            aria-label="Toggle billing period"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${billing === 'yearly' ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
          <span className={`text-sm font-medium ${billing === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
            Yearly
          </span>
          {billing === 'yearly' && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              Save 2 months
            </span>
          )}
        </div>
      </section>
      {/* Pricing Cards */}
      <section className="py-10 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {/* Free */}
            <div className="border border-gray-200 rounded-2xl p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900">Free</h2>
                <p className="text-gray-500 text-sm mt-1">Start building habits at no cost.</p>
                <div className="mt-4">
                  <span className="text-4xl font-extrabold text-gray-900">Free</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                {['Basic Dashboard', '2 x Health habits', '2 x Fitness habits', 'Weekly tracker']?.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button className="w-full py-3 px-4 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl transition-all text-sm">
                Get Started Free
              </button>
            </div>

            {/* Basic */}
            <div className="border border-gray-200 rounded-2xl p-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900">Basic</h2>
                <p className="text-gray-500 text-sm mt-1">Perfect for individuals building new habits.</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-gray-900">{basicPrice}</span>
                  <span className="text-gray-500 text-sm">{period}</span>
                </div>
                {billing === 'yearly' && (
                  <p className="text-xs text-green-600 font-medium mt-1">Equivalent to $4.99/month</p>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'Advanced Dashboard',
                  'Unlimited habits (across all categories)',
                  'Weekly tracker',
                  'Streak tracking',
                  'Weight tracking',
                ]?.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button className="w-full py-3 px-4 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl transition-all text-sm">
                Get Basic
              </button>
            </div>

            {/* Advanced */}
            <div className="rounded-2xl p-6 relative" style={{ background: 'linear-gradient(to bottom right, #7c3aed, #6d28d9)' }}>
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  Most Popular
                </span>
              </div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white">Advanced</h2>
                <p className="text-violet-200 text-sm mt-1">For serious habit builders who want every edge.</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-white">{advancedPrice}</span>
                  <span className="text-violet-200 text-sm">{period}</span>
                </div>
                {billing === 'yearly' && (
                  <p className="text-xs text-violet-200 font-medium mt-1">Equivalent to $8.33/month</p>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'Advanced, editable Dashboard',
                  'Advanced Dashboard analytics',
                  'Unlimited habits (across all categories)',
                  'Weekly tracker',
                  'Streak tracking',
                  'Weight tracking',
                  'Achievements & gamification',
                  'XP and levelling system',
                  'Workout creation, management and tracking',
                  'Advanced AI-powered insights',
                ]?.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-white">
                    <CheckIconWhite />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-violet-700 font-semibold rounded-xl transition-all text-sm">
                Get Advanced
              </button>
            </div>
          </div>

          {/* Coupon */}
          <div className="flex items-center justify-center gap-3 mt-10">
            <input
              type="text"
              placeholder="Have a coupon code?"
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
            />
            <button className="px-4 py-2.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-xl transition-all">
              Apply
            </button>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            Already have an account?{' '}
            <button className="text-violet-600 hover:text-violet-700 font-medium">Log in</button>
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
